import type { ChangeEvent, FC, FormEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Spacer, Typography } from "@aviary";
import { AppleSSOButton, GoogleSSOButton } from "@shared/components";
import { isEmailValid } from "@shared/components/EmailInput";
import { useBreakpoints, useFlippers } from "@shared/hooks";
import { isPasswordValid, PasswordInput } from "@unauthenticated/shared/components/PasswordInput";
import { useGlobalConfig } from "@unauthenticated/shared/context";
import { useAccountExistsCheck } from "@unauthenticated/shared/hooks";
import { l } from "@unauthenticated/shared/locales/i18n";

import { FormWrapper } from "../../FormWrapper";
import { PractitionerSignUpEmailInput } from "../../PractitionerSignUpEmailInput";
import { SignUpCarouselOrFooter } from "../../SignUpCarouselOrFooter";
import * as styles from "../SimplifySignUp.styles";
import type { SimplifySignUpWithSignUpPageProps } from "../types";

const PageTwo: FC<SimplifySignUpWithSignUpPageProps> = ({
  formData,
  errors,
  setFormData,
  logField,
  onSignInRedirect,
  onSubmit,
  additionalAttributes,
  pageVersion,
  signUpPage,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();
  const { tablet } = useBreakpoints();
  const [isAppleSSO] = useFlippers("apple_sso");

  const [emailDirty, setEmailDirty] = useState(false);
  const [passwordDirty, setPasswordDirty] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));

    // This component will fire the onChange event to display any existing
    // formData value in which case we don't want to delete the error
    if (formData[name] !== value && errors?.[name]) {
      delete errors[name];
    }
  };

  const setEmail = (email: string) => {
    setFormData(prevState => ({ ...prevState, email }));
  };

  const {
    isAccountExists,
    setIsAccountExists,
    handleCheckIfAccountExists,
    checkIfAccountExistsWithRecaptcha,
  } = useAccountExistsCheck();

  const onEmailBlur = e => {
    if (isEmailValid(formData.email, emailRegex)) {
      logField("email");
    }
    setEmailDirty(true);
    if (e?.target.value) {
      handleChange(e);
      handleCheckIfAccountExists(e.target.value);
    }
  };

  const onPasswordBlur = () => {
    if (isPasswordValid(formData.password)) {
      logField("password");
    }
  };

  const isFormValid = () => {
    const emailValid = isEmailValid(formData.email, emailRegex) && !isAccountExists;
    const passwordValid = isPasswordValid(formData.password);
    return passwordValid && emailValid;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    onSubmit();
  };

  return (
    <form
      role="form"
      onSubmit={handleSubmit}
      css={styles.form}
      noValidate
      data-e2e="practitioner-sign-up-form"
      data-gtm-id="prac-sign-up-form"
    >
      <div css={styles.contentBox}>
        <Typography type="h2">
          {t(l.practitionerSignUp.HowToCompleteYourAccount, { firstName: formData.firstName })}
        </Typography>
        <Typography css={styles.subTitle}>{t(l.practitionerSignUp.SelectSignUpMethod)}</Typography>
        <FormWrapper>
          <div css={styles.fullWidth}>
            <GoogleSSOButton
              isOrTextRendered={!isAppleSSO}
              pracTypeId={formData?.practitionerType?.id}
              pageVersion={pageVersion}
              additionalAttributes={additionalAttributes}
              signUpPage={signUpPage}
              formData={{
                firstName: formData?.firstName,
                lastName: formData?.lastName,
                storeName: formData?.dispensaryName,
                tosAcceptedAt: formData?.tosTimestamp,
              }}
            />
            {isAppleSSO && (
              <AppleSSOButton
                pracTypeId={formData?.practitionerType?.id}
                pageVersion={pageVersion}
                additionalAttributes={additionalAttributes}
                isOrTextRendered
                buttonStyles={styles.appleButton}
                signUpPage={signUpPage}
                formData={{
                  firstName: formData?.firstName,
                  lastName: formData?.lastName,
                  storeName: formData?.dispensaryName,
                  tosAcceptedAt: formData?.tosTimestamp,
                }}
              />
            )}
          </div>
          <PractitionerSignUpEmailInput
            value={formData.email}
            onChange={handleChange}
            setEmail={setEmail}
            isDirty={emailDirty}
            required
            isAccountExists={isAccountExists}
            setIsAccountExists={setIsAccountExists}
            handleCheckIfAccountExists={handleCheckIfAccountExists}
            checkIfAccountExistsWithRecaptcha={checkIfAccountExistsWithRecaptcha}
            onSignInRedirect={onSignInRedirect}
            onBlur={onEmailBlur}
            wrapperStyles={styles.input}
          />
          <PasswordInput
            value={formData.password}
            onChange={handleChange}
            isLoading={false}
            required
            isDirty={passwordDirty}
            onFocus={() => setPasswordDirty(true)}
            onBlur={onPasswordBlur}
            wrapperStyles={styles.input}
            errors={errors?.password}
          />
          <Button
            type="submit"
            isFullWidth
            data-e2e="create-practitioner-button"
            disabled={isLoading || !isFormValid()}
            isLoading={isLoading}
          >
            {t(l.common.CreateAccount)}
          </Button>
        </FormWrapper>
        {tablet.greaterThan && (
          <>
            <Spacer height="quad" />
            <SignUpCarouselOrFooter isWithinViewport={tablet.greaterThan} />
          </>
        )}
      </div>
    </form>
  );
};

export { PageTwo };
